import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, catchError, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  PaginatedDropdownLoadParams,
  PaginatedOptionsPage,
} from '@shared/utils/paginated-dropdown.util';
import { createClientSidePaginatedLoader } from '@shared/utils/paginated-field.helpers';

export interface Province {
  id: number;
  name: string;
  code: string;
  full_name: string;
  name_en: string;
  full_name_en: string;
}

export interface ProvinceOption {
  label: string;
  value: number;
}

@Injectable({
  providedIn: 'root',
})
export class ProvinceService {
  private readonly http = inject(HttpClient);
  private readonly translateService = inject(TranslateService);

  /**
   * Load provinces from JSON file
   */
  getProvinces(): Observable<Province[]> {
    return this.http.get<Province[]>('assets/data/vietnam-provinces.json').pipe(
      catchError((error) => {
        console.error('Error loading provinces:', error);
        return of([]);
      }),
    );
  }

  /**
   * Get provinces as dropdown options with localized names
   */
  getProvinceOptions(): Observable<ProvinceOption[]> {
    return this.mapProvincesToOptions(this.getProvinces());
  }

  getProvinceOptionsPage(
    params: PaginatedDropdownLoadParams,
  ): Observable<PaginatedOptionsPage<number>> {
    return createClientSidePaginatedLoader<number>(() =>
      this.getProvinceOptions(),
    )(params);
  }

  private mapProvincesToOptions(
    source: Observable<Province[]>,
  ): Observable<ProvinceOption[]> {
    return source.pipe(
      map((provinces) => {
        const currentLang =
          ((this.translateService as { currentLang?: string }).currentLang as
            | string
            | undefined) || 'vi';

        return provinces.map((province) => ({
          label:
            currentLang === 'en' ? province.full_name_en : province.full_name,
          value: province.id,
        }));
      }),
    );
  }

  /**
   * Get province by ID
   */
  getProvinceById(id: number): Observable<Province | null> {
    return this.getProvinces().pipe(
      map((provinces) => provinces.find((p) => p.id === id) || null),
    );
  }
}
